import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { api } from '@/lib/api-client';
import { WizardStepAppInfo } from './wizard-step-app-info';
import {
    createDefaultFormData,
    fromIntegrationManifest,
    toIntegrationManifest,
    type AppInfoFormData,
    type IntegrationAuth,
    type McpConfig,
    type McpDiscoveredAuth,
} from '@/types/integration-form-types';

const LOG_PREFIX = 'integration-overview-tab';

interface IntegrationOverviewTabProps {
    typeConfig: {
        protocol?: string;
        base_url?: string;
        mcp?: Partial<Omit<McpConfig, 'transport'>> & { transport?: string; server_url?: string };
        auth?: {
            supported_types?: string[];
            strategies?: Record<string, unknown>;
        };
        app?: {
            name?: string;
            vendor?: string;
            category?: string;
            description?: string;
            icon_url?: string;
            documentation_url?: string;
        };
        common_headers?: Record<string, string>;
    };
    tags?: string[];
    displayName?: string;
    description?: string;
    onDeferredDirtyChange?: (payload: { dirty: boolean; patch: Record<string, unknown> }) => void;
    onPersist?: (patch: Record<string, unknown>) => Promise<boolean>;
    disabled?: boolean;
    toolCapabilitySlot?: ReactNode;
}

export interface McpDiscoverApiResult {
    ok: boolean;
    server_url: string;
    protocol_version?: string;
    features: string[];
    server_info?: { name?: string; version?: string };
    tools?: { name?: string; description?: string }[];
    resources?: { name?: string; uri?: string; description?: string }[];
    prompts?: { name?: string; description?: string }[];
    auth: McpDiscoveredAuth;
    error?: string;
}

function mcpFromTypeConfig(tc: IntegrationOverviewTabProps['typeConfig']): McpConfig | undefined {
    const mcp = tc.mcp;
    if (!mcp) return undefined;
    const serverUrl = mcp.server_url || tc.base_url || '';
    if (!serverUrl && !mcp.features?.length && !mcp.discovered_auth) return undefined;
    return {
        transport: mcp.transport === 'streamable_http' ? 'streamable_http' : 'streamable_http',
        server_url: serverUrl,
        discovery: mcp.discovery ?? true,
        features: mcp.features,
        server_info: mcp.server_info,
        discovered_auth: mcp.discovered_auth,
        tools: mcp.tools,
        resources: mcp.resources,
        prompts: mcp.prompts,
    };
}

function resultFromMcp(mcp: McpConfig | undefined): McpDiscoverApiResult | null {
    if (!mcp?.features?.length && !mcp?.discovered_auth && !mcp?.tools?.length) {
        return null;
    }
    return {
        ok: true,
        server_url: mcp.server_url,
        features: mcp.features ?? [],
        server_info: mcp.server_info,
        tools: mcp.tools,
        resources: mcp.resources,
        prompts: mcp.prompts,
        auth: mcp.discovered_auth ?? { required: false, method: 'none' },
    };
}

function mcpFromDiscoverResult(url: string, result: McpDiscoverApiResult): McpConfig {
    return {
        transport: 'streamable_http',
        server_url: url,
        discovery: true,
        features: result.features,
        server_info: result.server_info,
        discovered_auth: result.auth,
        tools: result.tools,
        resources: result.resources,
        prompts: result.prompts,
    };
}

function usesPkce(auth: McpDiscoveredAuth, isAuthCode: boolean): boolean {
    const methods = auth.code_challenge_methods_supported ?? [];
    if (methods.includes('S256') || methods.includes('plain')) return true;
    return isAuthCode;
}

function authFromDiscovery(auth: McpDiscoveredAuth): IntegrationAuth | undefined {
    if (auth.method === 'oauth2') {
        const grants = auth.grant_types_supported ?? [];
        const clientCredsOnly =
            grants.includes('client_credentials') && !grants.includes('authorization_code');
        const grant = clientCredsOnly ? 'oauth2_client_credentials' : 'oauth2_authorization_code';
        const tokenUrl = auth.token_endpoint ?? '';
        return {
            supported_types: [grant],
            strategies: {
                [grant]: {
                    auth_url: auth.authorization_endpoint ?? '',
                    token_url: tokenUrl,
                    refresh_url: auth.refresh_endpoint || tokenUrl || undefined,
                    scopes: auth.scopes_supported,
                    pkce: usesPkce(auth, grant === 'oauth2_authorization_code'),
                    token_injection: {
                        location: 'header',
                        key: 'Authorization',
                        template: 'Bearer {{access_token}}',
                    },
                },
            },
        };
    }
    if (auth.method === 'bearer_token') {
        return {
            supported_types: ['bearer_token'],
            strategies: {
                bearer_token: {
                    required_fields: ['access_token'],
                    token_injection: {
                        location: 'header',
                        key: 'Authorization',
                        template: 'Bearer {{access_token}}',
                    },
                },
            },
        };
    }
    return undefined;
}

function authMethodLabel(method: string | undefined): string {
    switch (method) {
        case 'oauth2':
            return 'OAuth 2.0';
        case 'bearer_token':
            return 'Bearer token';
        case 'api_key':
            return 'API key';
        case 'none':
        case undefined:
            return 'None';
        default:
            return method;
    }
}

function mergeDiscoveredAuth(
    existing: IntegrationOverviewTabProps['typeConfig']['auth'],
    discovered: IntegrationAuth,
): IntegrationAuth {
    const grant = discovered.supported_types[0];
    const existingStrat = (existing?.strategies?.[grant] ?? {}) as Record<string, unknown>;
    const discoveredStrat = (discovered.strategies[grant] ?? {}) as Record<string, unknown>;
    return {
        supported_types: discovered.supported_types,
        strategies: {
            ...(existing?.strategies as IntegrationAuth['strategies']),
            [grant]: { ...existingStrat, ...discoveredStrat },
        },
    };
}

export function IntegrationOverviewTab({
    typeConfig,
    tags = [],
    displayName = '',
    description = '',
    onDeferredDirtyChange,
    onPersist,
    disabled = false,
    toolCapabilitySlot,
}: IntegrationOverviewTabProps) {
    const formData = useMemo(() => fromIntegrationManifest(typeConfig, tags), [typeConfig, tags]);
    const [appInfo, setAppInfo] = useState<AppInfoFormData>(() => {
        const next = formData.appInfo;
        return {
            ...next,
            name: next.name || displayName,
            description: next.description || description,
        };
    });
    const [discovery, setDiscovery] = useState<McpDiscoverApiResult | null>(() =>
        resultFromMcp(mcpFromTypeConfig(typeConfig)),
    );
    const [discovering, setDiscovering] = useState(false);
    const lastDirtyPayloadRef = useRef<string | null>(null);

    useEffect(() => {
        setAppInfo((prev) => {
            const next = {
                ...formData.appInfo,
                name: formData.appInfo.name || displayName,
                description: formData.appInfo.description || description,
            };
            if (JSON.stringify(prev) === JSON.stringify(next)) {
                return prev;
            }
            return next;
        });
        setDiscovery(resultFromMcp(mcpFromTypeConfig(typeConfig)));
    }, [formData.appInfo, displayName, description, typeConfig]);

    const baselineSerialized = useMemo(
        () => JSON.stringify({
            appInfo: {
                ...formData.appInfo,
                name: formData.appInfo.name || displayName,
                description: formData.appInfo.description || description,
            },
            mcp: mcpFromTypeConfig(typeConfig) ?? null,
        }),
        [formData.appInfo, displayName, description, typeConfig],
    );
    const currentSerialized = useMemo(
        () => JSON.stringify({
            appInfo,
            mcp: discovery ? mcpFromDiscoverResult(appInfo.baseUrl, discovery) : (mcpFromTypeConfig(typeConfig) ?? null),
        }),
        [appInfo, discovery, typeConfig],
    );
    const isDirty = baselineSerialized !== currentSerialized;

    const patch = useMemo(() => {
        const fullForm = { ...createDefaultFormData(), ...formData, appInfo };
        const { manifest, tags: updatedTags, description: desc } = toIntegrationManifest(fullForm);
        const discoveredAuth = discovery ? authFromDiscovery(discovery.auth) : undefined;
        const mcp = discovery
            ? mcpFromDiscoverResult(appInfo.baseUrl, discovery)
            : (typeConfig.mcp ?? manifest.mcp);
        const auth = discoveredAuth
            ? mergeDiscoveredAuth(typeConfig.auth, discoveredAuth)
            : undefined;
        return {
            type_config: {
                ...typeConfig,
                protocol: manifest.protocol,
                app: manifest.app,
                base_url: manifest.base_url,
                common_headers: manifest.common_headers,
                ...(mcp ? { mcp } : {}),
                ...(auth ? { auth } : {}),
            },
            tags: ['integration', ...updatedTags.filter((t) => t !== 'integration')],
            description: desc || null,
            display_name: appInfo.name || null,
        };
    }, [formData, appInfo, typeConfig, discovery]);

    useEffect(() => {
        if (!onDeferredDirtyChange) return;
        const payload = { dirty: isDirty, patch: isDirty ? patch : {} };
        const serialized = JSON.stringify(payload);
        if (lastDirtyPayloadRef.current === serialized) return;
        lastDirtyPayloadRef.current = serialized;
        onDeferredDirtyChange(payload);
    }, [isDirty, patch, onDeferredDirtyChange]);

    const handleDiscover = useCallback(async () => {
        const url = appInfo.baseUrl.trim();
        if (!url || disabled) return;
        setDiscovering(true);
        try {
            const res = await api.post('/integrations/mcp-discover', { url });
            const result = res.data as McpDiscoverApiResult;
            if (!result?.ok) {
                toast.error(result?.error || 'Discovery failed');
                if (result) setDiscovery(result);
                return;
            }
            const nextAppInfo: AppInfoFormData = {
                ...appInfo,
                protocol: 'mcp',
                name: appInfo.name || result.server_info?.name || '',
            };
            setDiscovery(result);
            setAppInfo(nextAppInfo);

            const discoveredAuth = authFromDiscovery(result.auth);
            const hasOAuthEndpoints = Boolean(
                result.auth?.authorization_endpoint || result.auth?.token_endpoint,
            );
            const persistPatch = {
                type_config: {
                    ...typeConfig,
                    protocol: 'mcp' as const,
                    app: {
                        ...typeConfig.app,
                        name: nextAppInfo.name,
                        description: nextAppInfo.description,
                    },
                    base_url: url,
                    mcp: mcpFromDiscoverResult(url, result),
                    ...(discoveredAuth
                        ? { auth: mergeDiscoveredAuth(typeConfig.auth, discoveredAuth) }
                        : {}),
                },
                display_name: nextAppInfo.name || null,
                description: nextAppInfo.description || null,
            };

            if (onPersist && discoveredAuth && hasOAuthEndpoints) {
                const saved = await onPersist(persistPatch);
                if (saved) {
                    toast.success('Saved OAuth authorize and token endpoints to this integration');
                } else {
                    toast.success('Discovered OAuth endpoints. Save the integration to keep them.');
                }
                return;
            }

            const featureList = result.features.length > 0 ? result.features.join(', ') : 'none advertised';
            toast.success(`Discovered ${featureList}. Auth: ${authMethodLabel(result.auth?.method)}`);
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Discovery failed';
            console.error(`${LOG_PREFIX}:handleDiscover - ${msg}`);
            toast.error(msg);
        } finally {
            setDiscovering(false);
        }
    }, [appInfo, disabled, onPersist, typeConfig]);

    return (
        <div className="space-y-4">
            <Card className="border-border/50 p-6">
                <CardHeader className={toolCapabilitySlot ? 'pb-0' : 'pb-3'}>
                    <CardTitle className="text-base">Integration Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                    <WizardStepAppInfo
                        value={appInfo}
                        onChange={(next) => {
                            if (disabled) return;
                            setAppInfo(next);
                        }}
                        errors={{}}
                        nameRowSlot={toolCapabilitySlot}
                        hideIdentityFields
                        urlLabel="MCP URL"
                        onDiscover={handleDiscover}
                        discovering={discovering}
                        discoverDisabled={disabled}
                        disabled={disabled}
                    />
                    {appInfo.protocol && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            <Badge variant="secondary" className="text-xs uppercase">
                                {appInfo.protocol}
                            </Badge>
                        </div>
                    )}
                </CardContent>
            </Card>

            {discovery && (
                <Card className="border-border/50 p-6">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Discovered MCP Server</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {discovery.server_info?.name && (
                            <p className="text-sm text-slate-600">
                                {discovery.server_info.name}
                                {discovery.server_info.version ? ` · v${discovery.server_info.version}` : ''}
                                {discovery.protocol_version ? ` · protocol ${discovery.protocol_version}` : ''}
                            </p>
                        )}
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5">Features</p>
                            <div className="flex flex-wrap gap-1.5">
                                {(discovery.features ?? []).length > 0 ? (
                                    discovery.features.map((feature) => (
                                        <Badge key={feature} variant="secondary" className="text-xs capitalize">
                                            {feature}
                                        </Badge>
                                    ))
                                ) : (
                                    <span className="text-sm text-slate-400">None advertised</span>
                                )}
                            </div>
                        </div>
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5">Auth method</p>
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="secondary" className="text-xs">
                                    {authMethodLabel(discovery.auth?.method)}
                                    {discovery.auth?.required ? ' · required' : ''}
                                </Badge>
                            </div>
                            {(discovery.auth?.authorization_endpoint || discovery.auth?.token_endpoint) && (
                                <dl className="mt-2 space-y-1 text-xs font-mono text-slate-600">
                                    {discovery.auth.issuer && (
                                        <div className="flex gap-2 min-w-0">
                                            <dt className="shrink-0 text-slate-400">issuer</dt>
                                            <dd className="truncate">{discovery.auth.issuer}</dd>
                                        </div>
                                    )}
                                    {discovery.auth.authorization_endpoint && (
                                        <div className="flex gap-2 min-w-0">
                                            <dt className="shrink-0 text-slate-400">authorize</dt>
                                            <dd className="truncate">{discovery.auth.authorization_endpoint}</dd>
                                        </div>
                                    )}
                                    {discovery.auth.token_endpoint && (
                                        <div className="flex gap-2 min-w-0">
                                            <dt className="shrink-0 text-slate-400">token</dt>
                                            <dd className="truncate">{discovery.auth.token_endpoint}</dd>
                                        </div>
                                    )}
                                    {discovery.auth.refresh_endpoint && (
                                        <div className="flex gap-2 min-w-0">
                                            <dt className="shrink-0 text-slate-400">refresh</dt>
                                            <dd className="truncate">{discovery.auth.refresh_endpoint}</dd>
                                        </div>
                                    )}
                                </dl>
                            )}
                        </div>
                        {(discovery.tools ?? []).length > 0 && (
                            <div>
                                <p className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-1.5">
                                    Tools ({discovery.tools!.length})
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {discovery.tools!.slice(0, 24).map((tool) => (
                                        <Badge key={tool.name} variant="outline" className="text-xs font-mono">
                                            {tool.name}
                                        </Badge>
                                    ))}
                                    {discovery.tools!.length > 24 && (
                                        <span className="text-xs text-slate-400">+{discovery.tools!.length - 24} more</span>
                                    )}
                                </div>
                            </div>
                        )}
                        {discovery.error && (
                            <p className="text-xs text-amber-600">{discovery.error}</p>
                        )}
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
